import type { TeamIntegration } from '@rhythmic/db/types'
import type { CreateTeamIntegrationPayload } from '@rhythmic/types'
import { CreateTeamIntegrationSchema, TeamIntegrationSchema } from '@rhythmic/types'
import type { Transaction } from '@tanstack/vue-db'
import { useRhythmicLogger } from '#imports'
import { getTeamIntegrationsCollection } from '../../contexts/getCollections'

const logger = useRhythmicLogger('useTeamIntegrationsContext:createTeamIntegration')

export interface CreateTeamIntegrationContext {
  teamId: string
}

function validateParams(payload: CreateTeamIntegrationPayload, context: CreateTeamIntegrationContext) {
  const { teamId } = context

  if (!teamId) {
    const error = new Error('Team context is not ready')
    logger.error('Unable to create team integration: missing team identifier')
    throw error
  }

  const parsed = CreateTeamIntegrationSchema.parse(payload)

  if (parsed.team_id !== teamId) {
    const error = new Error('Payload team identifier does not match active team context')
    logger.error('Unable to create team integration: mismatched team identifier', {
      payloadTeamId: parsed.team_id,
      contextTeamId: teamId,
    })
    throw error
  }

  return parsed
}

export default function handleCreateTeamIntegration(
  payload: CreateTeamIntegrationPayload,
  context: CreateTeamIntegrationContext,
): Transaction<TeamIntegration> {
  try {
    const validated = validateParams(payload, context)
    const collection = getTeamIntegrationsCollection(context.teamId)

    const timestamp = new Date()
    const integration = TeamIntegrationSchema.parse({
      ...validated,
      created_at: timestamp,
      updated_at: timestamp,
    })

    const transaction = collection.insert(integration) as Transaction<TeamIntegration>

    logger.info('Team integration created via collection', {
      teamId: context.teamId,
      integrationId: integration.id,
      providerId: integration.integration_id,
    })

    return transaction
  } catch (error) {
    logger.error('createTeamIntegration failed', { payload, error })
    if (error instanceof Error) throw error
    throw new Error('Failed to create team integration')
  }
}
